/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody } from "@nextui-org/modal";
import {
  Dropdown,
  DropdownMenu,
  DropdownTrigger,
  DropdownItem,
} from "@nextui-org/dropdown";
import { Button } from "@nextui-org/button";
import { ReactComponent as ChevronDown } from "../assets/icons/chevron-down.svg";
import useApproverStore from "../stores/useApproverStore";

const SelectApproverModal = ({
  isOpen,
  onOpenChange,
  roles,
  isSeperateRole = true,
  onConfirm,
}) => {
  const [selectedApprovers, setSelectedApprovers] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);

  const { approvers, fetchApprovers } = useApproverStore();

  const filteredApprovers = new Map(
    Array.from(approvers).filter(([role]) => roles.includes(role))
  );

  useEffect(() => {
    fetchApprovers();
  }, []);

  useEffect(() => {
    setInternalIsOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (!internalIsOpen) {
      setSelectedApprovers({});
      const style = document.createElement("style");
      style.innerHTML = `
        .nextui-c-dropdown-menu {
          max-height: 300px;
          overflow-y: auto;
        }
      `;
      document.head.appendChild(style);
      return () => style.remove();
    }
  }, [internalIsOpen]);

  const handleOpenChange = useCallback(
    (open) => {
      if (!open && !dropdownOpen) {
        setInternalIsOpen(open);
        onOpenChange?.(open);
      } else if (open) {
        setInternalIsOpen(open);
        onOpenChange?.(open);
      }
    },
    [dropdownOpen, onOpenChange]
  );

  const handleDropdownOpenChange = (open) => {
    setDropdownOpen(open);
    if (open) {
      setInternalIsOpen(true);
    }
  };

  const onSelectApprover = (roleId, user) => {
    if (isSeperateRole) {
      setSelectedApprovers((prev) => ({
        ...prev,
        [roleId]: user,
      }));
    } else {
      setSelectedApprovers({
        [roleId]: user,
      });
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedApprovers);
  };

  const renderDropdowns = () => {
    if (isSeperateRole) {
      return Array.from(filteredApprovers).map(([role, users]) => (
        <div key={role} className="flex flex-row items-center gap-y-2 gap-x-4">
          <h3 className="font-semibold text-nowrap">
            ผู้อนุมัติ ลำดับที่ {role.split("L").pop()}
          </h3>
          <Dropdown
            className="border-1 border-light-gray-300"
            onOpenChange={handleDropdownOpenChange}
            isDisabled={!internalIsOpen}
          >
            <DropdownTrigger>
              <Button
                className="w-full bg-white border-1 border-light-gray-300"
                endContent={<ChevronDown className="h-4 w-4" />}
                aria-label={`Select ${role} approver`}
              >
                <div className="flex w-full justify-between items-center">
                  <span>
                    {selectedApprovers[role]?.name || `เลือกผู้อนุมัติ`}
                  </span>
                </div>
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              className="text-white max-h-44 overflow-auto"
              aria-label={`${role} approver options`}
              onPointerDownOutside={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {users.map((user) => (
                <DropdownItem
                  key={user.id}
                  onPress={() => onSelectApprover(role, user)}
                  className="text-black"
                  textValue={user.id}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{user.name}</span>
                    <span className="text-sm text-gray-500">{user.email}</span>
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      ));
    } else {
      const allUsers = Array.from(filteredApprovers).flatMap(([role, users]) =>
        users.map((user) => ({ ...user, role_name: role }))
      );

      return (
        <div className="flex flex-row items-center gap-y-2 gap-x-4">
          <h3 className="font-semibold text-nowrap">ผู้อนุมัติ ลำดับที่ 2</h3>
          <Dropdown
            className="border-1 border-light-gray-300"
            onOpenChange={handleDropdownOpenChange}
            isDisabled={!internalIsOpen}
          >
            <DropdownTrigger>
              <Button
                className="w-full bg-white border-1 border-light-gray-300"
                endContent={<ChevronDown className="h-4 w-4" />}
                aria-label="Select approver"
              >
                <div className="flex w-full justify-between items-center">
                  <span>
                    {Object.values(selectedApprovers)[0]?.name ||
                      "เลือกผู้อนุมัติ"}
                  </span>
                </div>
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              className="text-white max-h-44 overflow-auto"
              aria-label="Second level approver options"
              onPointerDownOutside={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {allUsers.map((user) => (
                <DropdownItem
                  key={`${user.id}-${user.role_name}`}
                  textValue={`${user.id}-${user.role_name}`}
                  onPress={() => onSelectApprover(user.role_name, user)}
                  className="text-black"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{user.name}</span>
                    <span className="text-sm text-gray-500">{user.email}</span>
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Modal
        isOpen={internalIsOpen}
        placement="center"
        onOpenChange={handleOpenChange}
        isDismissable={!dropdownOpen}
        closeButton={
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="white"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: "absolute",
              right: "16px",
              top: "14px",
              background: "transparent",
            }}
          >
            <path
              d="M6 18L18 6M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="bg-dark-gray text-white">
                โปรดเลือกผู้อนุมัติ
              </ModalHeader>
              <ModalBody className="flex flex-col gap-y-7 justify-center py-6 px-1 mx-5">
                {renderDropdowns()}
                <div className="flex flex-row justify-end items-end gap-x-3">
                  <Button
                    isDisabled={
                      Object.entries(selectedApprovers).length !==
                      Array.from(filteredApprovers).length
                    }
                    className="bg-blue-500 text-white"
                    onPress={handleConfirm}
                  >
                    บันทึก
                  </Button>
                  <Button
                    className="bg-light-gray-300 text-white "
                    variant="light"
                    onPress={() => {
                      if (onClose) {
                        onClose();
                      }
                    }}
                  >
                    ยกเลิก
                  </Button>
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default SelectApproverModal;
